/**
 * 结算关注飞机配置弹窗(2026-05-13 用户决策)。
 *
 * 业务语义:
 *  - 用户从 LegCostFilter "配置飞机"按钮打开本弹窗
 *  - 列出所有 master-data 飞机,默认全部 tracked
 *  - 反选某些飞机 → 它们立刻从"航段费用查询" / KPI / 异常清单中消失(全屏蔽)
 *
 * 数据模型:
 *  - GET  /v3/settlement/aircraft-tracking      → 列表 + 统计
 *  - PUT  /v3/settlement/aircraft-tracking      → 批量 upsert(diff 后只提交变化的)
 *
 * 设计选择:
 *  - 不允许编辑 registration / 机型(只读),改这些去 /master-data/aircraft
 *  - 本地 dirty 标记 → 关闭弹窗前给确认对话框,避免误操作
 *  - 搜索过滤不影响 dirty 状态(只是视图筛选)
 */

import React, { useEffect, useMemo, useState } from "react";
import {
    Modal,
    Table,
    Switch,
    Input,
    Tag,
    Button,
    Space,
    Alert,
    Tooltip,
    message,
    Popconfirm,
    Typography,
} from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { settlementApi } from "../../../../api/settlementApi";

const { Text } = Typography;

const AIRCRAFT_STATUS_COLORS = {
    ACTIVE: "green",
    MAINTENANCE: "orange",
    INACTIVE: "default",
    STORED: "default",
    RETIRED: "red",
};

const AircraftTrackingModal = ({ open, onClose, onSaved }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [aircraftList, setAircraftList] = useState([]);
    const [searchText, setSearchText] = useState("");
    // dirtyMap: { [aircraft_id]: is_tracked_new_value } — 只存"被改过的"飞机
    const [dirtyMap, setDirtyMap] = useState({});

    const fetchList = async () => {
        try {
            setLoading(true);
            const resp = await settlementApi.dataQuality.listAircraftTracking();
            const data = resp?.data || resp;
            setAircraftList(data.items || []);
            setDirtyMap({});
        } catch (err) {
            console.error("加载飞机配置失败:", err);
            message.error("加载飞机配置失败: " + (err.message || "未知错误"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchList();
            setSearchText("");
        }
    }, [open]);

    const handleToggle = (aircraftId, originalTracked, newTracked) => {
        setDirtyMap((prev) => {
            const next = { ...prev };
            if (newTracked === originalTracked) {
                // 改回了原始值,移除 dirty
                delete next[aircraftId];
            } else {
                next[aircraftId] = newTracked;
            }
            return next;
        });
    };

    const handleSave = async () => {
        const items = Object.entries(dirtyMap).map(([aircraft_id, is_tracked]) => ({
            aircraft_id: Number(aircraft_id),
            is_tracked,
        }));
        if (items.length === 0) {
            message.info("没有改动需要保存");
            return;
        }
        try {
            setSaving(true);
            const resp = await settlementApi.dataQuality.bulkUpdateAircraftTracking(items);
            const data = resp?.data || resp;
            message.success(
                `保存成功:更新 ${data.updated_count} 条,当前跟踪 ${data.tracked_count} 架、隐藏 ${data.excluded_count} 架`
            );
            setDirtyMap({});
            if (typeof onSaved === "function") {
                onSaved(data);
            }
            await fetchList();
        } catch (err) {
            console.error("保存飞机配置失败:", err);
            message.error("保存失败: " + (err.response?.data?.detail || err.message || "未知错误"));
        } finally {
            setSaving(false);
        }
    };

    const handleSelectAll = (targetValue) => {
        const next = {};
        for (const ac of aircraftList) {
            if (ac.is_tracked !== targetValue) {
                next[ac.aircraft_id] = targetValue;
            }
        }
        setDirtyMap(next);
    };

    const handleClose = () => {
        const dirtyCount = Object.keys(dirtyMap).length;
        if (dirtyCount > 0) {
            Modal.confirm({
                title: "确认放弃改动?",
                content: `有 ${dirtyCount} 条未保存的改动,关闭后将丢失。`,
                okText: "放弃改动",
                cancelText: "继续编辑",
                okButtonProps: { danger: true },
                onOk: () => {
                    setDirtyMap({});
                    onClose && onClose();
                },
            });
        } else {
            onClose && onClose();
        }
    };

    const filteredList = useMemo(() => {
        if (!searchText.trim()) return aircraftList;
        const kw = searchText.trim().toLowerCase();
        return aircraftList.filter(
            (ac) =>
                (ac.registration || "").toLowerCase().includes(kw) ||
                (ac.aircraft_type || "").toLowerCase().includes(kw)
        );
    }, [aircraftList, searchText]);

    // 当前展示状态(应用 dirtyMap 覆盖)
    const getEffectiveTracked = (ac) => {
        if (Object.prototype.hasOwnProperty.call(dirtyMap, ac.aircraft_id)) {
            return dirtyMap[ac.aircraft_id];
        }
        return ac.is_tracked;
    };

    const totalTracked = aircraftList.filter(getEffectiveTracked).length;
    const totalExcluded = aircraftList.length - totalTracked;
    const dirtyCount = Object.keys(dirtyMap).length;

    const columns = [
        {
            title: "飞机注册号",
            dataIndex: "registration",
            key: "registration",
            width: 130,
            sorter: (a, b) => (a.registration || "").localeCompare(b.registration || ""),
            render: (text) => <Text strong>{text}</Text>,
        },
        {
            title: "机型",
            dataIndex: "aircraft_type",
            key: "aircraft_type",
            width: 110,
            render: (t) => t || <Text type="secondary">-</Text>,
        },
        {
            title: "运营状态",
            dataIndex: "aircraft_status",
            key: "aircraft_status",
            width: 100,
            render: (s) => {
                if (!s) return <Text type="secondary">-</Text>;
                return <Tag color={AIRCRAFT_STATUS_COLORS[s] || "default"}>{s}</Tag>;
            },
        },
        {
            title: (
                <span>
                    纳入结算{" "}
                    <Tooltip title="开启=出现在航段费用查询/KPI/异常清单中;关闭=完全隐藏,即便有账单也不显示">
                        <Text type="secondary" style={{ cursor: "help" }}>
                            ?
                        </Text>
                    </Tooltip>
                </span>
            ),
            key: "is_tracked",
            width: 140,
            align: "center",
            render: (_, record) => {
                const effective = getEffectiveTracked(record);
                const isDirty = Object.prototype.hasOwnProperty.call(dirtyMap, record.aircraft_id);
                return (
                    <Space>
                        <Switch
                            checked={effective}
                            onChange={(checked) =>
                                handleToggle(record.aircraft_id, record.is_tracked, checked)
                            }
                            checkedChildren="纳入"
                            unCheckedChildren="隐藏"
                        />
                        {isDirty && <Tag color="orange">未保存</Tag>}
                    </Space>
                );
            },
        },
        {
            title: "最后修改",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 160,
            render: (t) => {
                if (!t) return <Text type="secondary">从未配置</Text>;
                return new Date(t).toLocaleString("zh-CN");
            },
        },
    ];

    return (
        <Modal
            title="结算关注飞机配置"
            open={open}
            onCancel={handleClose}
            width={900}
            destroyOnClose
            footer={[
                <Button key="refresh" icon={<ReloadOutlined />} onClick={fetchList} disabled={saving}>
                    重新加载
                </Button>,
                <Button key="cancel" onClick={handleClose} disabled={saving}>
                    取消
                </Button>,
                <Popconfirm
                    key="save"
                    title={`确认保存 ${dirtyCount} 条改动?`}
                    description="保存后,被关闭的飞机将立即从所有结算列表中消失。"
                    onConfirm={handleSave}
                    okText="保存"
                    cancelText="再想想"
                    disabled={dirtyCount === 0}
                >
                    <Button type="primary" loading={saving} disabled={dirtyCount === 0}>
                        保存改动 ({dirtyCount})
                    </Button>
                </Popconfirm>,
            ]}
        >
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message="哪些飞机不需要进结算?在这里关掉。"
                description={
                    <>
                        关闭"纳入结算"后,该飞机的所有航班 / 账单 / 异常会从"航段费用查询"、"KPI 卡片"、"数据异常"全部隐藏。
                        默认所有飞机都开启;新飞机自动开启。
                        改了 registration / 机型请去 <Text code>/master-data/aircraft</Text>。
                    </>
                }
            />

            <Space style={{ marginBottom: 12, width: "100%", justifyContent: "space-between" }}>
                <Input
                    placeholder="搜索注册号 / 机型"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                    style={{ width: 260 }}
                />
                <Space>
                    <Text type="secondary">
                        当前:纳入 <Text strong>{totalTracked}</Text> / 隐藏{" "}
                        <Text strong type={totalExcluded > 0 ? "warning" : undefined}>
                            {totalExcluded}
                        </Text>{" "}
                        / 共 {aircraftList.length} 架
                    </Text>
                    <Button size="small" onClick={() => handleSelectAll(true)}>
                        全部纳入
                    </Button>
                    <Button size="small" onClick={() => handleSelectAll(false)} danger>
                        全部隐藏
                    </Button>
                </Space>
            </Space>

            <Table
                rowKey="aircraft_id"
                size="small"
                loading={loading}
                dataSource={filteredList}
                columns={columns}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (t) => `共 ${t} 架`,
                }}
                scroll={{ y: 420 }}
            />
        </Modal>
    );
};

export default AircraftTrackingModal;
